import { useEffect, useRef } from "react";

/**
 * ResponsiveTable
 *
 * Wraps an Ant Design <Table> and automatically injects `data-label`
 * attributes on every <td> by reading the matching <th> header text.
 * This allows the CSS `::before { content: attr(data-label) }` trick
 * to display column names in the mobile card layout — without touching
 * any <Column> prop.
 *
 * Usage:
 *   <ResponsiveTable>
 *     <Table ...>
 *       <Column title="Order ID" ... />
 *     </Table>
 *   </ResponsiveTable>
 */
export default function ResponsiveTable({ children, className = "" }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const applyLabels = () => {
      const container = containerRef.current;
      if (!container) return;

      const tables = container.querySelectorAll("table");
      tables.forEach((table) => {
        const theads = [];
        const tbodies = [];
        for (let i = 0; i < table.children.length; i++) {
          const child = table.children[i];
          const tagName = child.tagName.toLowerCase();
          if (tagName === "thead") {
            theads.push(child);
          } else if (tagName === "tbody") {
            tbodies.push(child);
          }
        }
        if (theads.length === 0 || tbodies.length === 0) return;

        // Take the primary thead (usually the first one)
        const thead = theads[0];

        // Get header row (last tr in thead)
        let headerRow = null;
        for (let i = 0; i < thead.children.length; i++) {
          const child = thead.children[i];
          if (child.tagName.toLowerCase() === "tr") {
            headerRow = child;
          }
        }
        if (!headerRow) return;

        const headers = [];
        for (let i = 0; i < headerRow.children.length; i++) {
          const child = headerRow.children[i];
          if (child.tagName.toLowerCase() === "th") {
            headers.push(child.innerText.trim());
          }
        }

        // Apply matching label to every td in every body row of all tbodies
        tbodies.forEach((tbody) => {
          for (let i = 0; i < tbody.children.length; i++) {
            const row = tbody.children[i];
            if (row.tagName.toLowerCase() !== "tr") continue;
            if (
              row.classList.contains("ant-table-expanded-row") ||
              row.classList.contains("ant-table-placeholder")
            ) {
              continue;
            }

            let cellIdx = 0;
            for (let j = 0; j < row.children.length; j++) {
              const cell = row.children[j];
              if (cell.tagName.toLowerCase() === "td") {
                if (headers[cellIdx] !== undefined) {
                  cell.setAttribute("data-label", headers[cellIdx]);
                }
                cellIdx++;
              }
            }
          }
        });
      });
    };

    // Run on mount
    applyLabels();

    // Re-run whenever the table re-renders (new data, pagination, etc.)
    const observer = new MutationObserver(applyLabels);
    if (containerRef.current) {
      observer.observe(containerRef.current, { childList: true, subtree: true });
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className={`respTableContainer ${className}`.trim()}>
      {children}
    </div>
  );
}
